import {CompanyResponse, useCompanies, useDealers, useUsers} from '@api'
import {NavigationPathKey, TranslationKey} from '@enums'
import {GridColDef, GridColumnVisibilityModel} from '@mui/x-data-grid'
import {pageLinksAtom} from '@state'

import {useEffect, useMemo} from 'react'

import {activeCell, isCompanyUser, roles, renderClickableCell, renderCompaniesCell, useViewNavigate} from 'common'
import {useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'

export interface UsersTableData {
  name: string
  companies?: CompanyResponse[]
  parentCompany?: string
  dealer: string
  email: string
  active: boolean
  role: string
}

export function useUserSearchModel() {
  const setTitleLinks = useSetAtom(pageLinksAtom)
  const {t} = useTranslation()
  const navigate = useViewNavigate()

  const {data} = useUsers()
  const {data: dealersData} = useDealers()
  const {data: companiesData} = useCompanies()

  const rows = useMemo(() => {
    if (!data) return []
    else
      return data.map(item => {
        const userCompanies = companiesData?.filter(company => item.companyIds.includes(company.id))

        return {
          name: item.fullName,
          email: item.email,
          active: item.active,
          company: userCompanies?.map(item => item.name).join(', '),
          companies: userCompanies,
          dealer: dealersData?.find(dealer => dealer.id === item.dealerId)?.name || '-',
          role: roles(t).find(roleItem => roleItem.code === item.role)?.name || '-',
          id: item.id,
        }
      })
  }, [data, companiesData, dealersData])

  useEffect(
    () =>
      setTitleLinks([
        {title: t(TranslationKey['Admin panel']), link: NavigationPathKey.ADMIN_PANEL},
        {title: t(TranslationKey.Resources), link: NavigationPathKey.RESOURCES_DASHBOARD},
        {title: t(TranslationKey['User search'])},
      ]),
    [],
  )

  /**
   * User table columns configuration
   */
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t(TranslationKey.Name),
      flex: 0.8,
      hideable: false,
      renderCell: params =>
        renderClickableCell(params, () => navigate(`${NavigationPathKey.USER_VIEW}/${params.row.id}`)),
    },
    {field: 'email', headerName: t(TranslationKey.Email), flex: 1},
    {field: 'dealer', headerName: t(TranslationKey.Dealer), flex: 1},
    {
      field: 'company',
      headerName: t(TranslationKey.Company),
      flex: 1,
      renderCell: params => renderCompaniesCell(params),
    },
    {field: 'parentCompany', headerName: t(TranslationKey['Amplio partner']), flex: 1},
    {field: 'role', headerName: t(TranslationKey.Role), flex: 0.8},
    activeCell(t),
  ]

  /**
   * User table visibility model configuration
   */
  const visibilityModel: GridColumnVisibilityModel = {
    parentCompany: false,
    dealer: false,
  }

  /**
   * Create button should be available only for Administrator users
   */
  const showCreateButton = !isCompanyUser()

  return {navigate, t, visibilityModel, showCreateButton, rows, columns}
}
